export const A2HS_PROMPT_STORAGE_KEY = "a2hs_prompt_v2";

export type A2hsPromptStatus =
  | "never_shown"
  | "dismissed"
  | "accepted"
  | "installed";

export interface A2hsPromptState {
  status: A2hsPromptStatus;
  pending?: boolean;
  shownAt?: string;
  updatedAt?: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const PROMPT_STATUSES = new Set<A2hsPromptStatus>([
  "never_shown",
  "dismissed",
  "accepted",
  "installed",
]);

export const parseA2hsPromptState = (raw: unknown): A2hsPromptState => {
  if (!isRecord(raw)) {
    return { status: "never_shown" };
  }

  const status = raw.status;
  if (
    typeof status === "string" &&
    PROMPT_STATUSES.has(status as A2hsPromptStatus)
  ) {
    return {
      status: status as A2hsPromptStatus,
      pending: raw.pending === true,
      shownAt: typeof raw.shownAt === "string" ? raw.shownAt : undefined,
      updatedAt: typeof raw.updatedAt === "string" ? raw.updatedAt : undefined,
    };
  }

  return { status: "never_shown" };
};

export const loadA2hsPromptState = (): A2hsPromptState => {
  if (typeof window === "undefined") {
    return { status: "never_shown" };
  }

  try {
    const saved = localStorage.getItem(A2HS_PROMPT_STORAGE_KEY);
    if (!saved) {
      return { status: "never_shown" };
    }
    return parseA2hsPromptState(JSON.parse(saved));
  } catch (error) {
    console.error("Error loading A2HS prompt state from localStorage:", error);
    return { status: "never_shown" };
  }
};

export const saveA2hsPromptState = (state: A2hsPromptState): void => {
  if (typeof window === "undefined") return;

  try {
    localStorage.setItem(A2HS_PROMPT_STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error("Error saving A2HS prompt state to localStorage:", error);
  }
};

export const setA2hsPromptStatus = (status: A2hsPromptStatus): void => {
  const previous = loadA2hsPromptState();
  const now = new Date().toISOString();

  saveA2hsPromptState({
    status,
    pending: false,
    shownAt: previous.shownAt ?? (status === "never_shown" ? undefined : now),
    updatedAt: now,
  });
};

/**
 * Queue the prompt after lesson 2. Re-queues on retakes until the user has
 * accepted or we detected an installed PWA. Also resets a previous dismiss.
 */
export const markA2hsPromptPending = (): void => {
  const previous = loadA2hsPromptState();
  if (previous.status === "accepted" || previous.status === "installed") {
    return;
  }

  saveA2hsPromptState({
    status: "never_shown",
    pending: true,
    shownAt: previous.shownAt,
    updatedAt: new Date().toISOString(),
  });
};

export const markA2hsPromptShown = (): void => {
  const previous = loadA2hsPromptState();
  const now = new Date().toISOString();

  // Keep `pending` true until accept/dismiss so a closed tab / failed show
  // can still surface the modal on the next home visit.
  saveA2hsPromptState({
    ...previous,
    shownAt: previous.shownAt ?? now,
    updatedAt: now,
  });
};
